/**
 * client/audio.ts
 * Responsabilidad: Gestion de audio de fondo (loop, volumen, mute, fade-in/out).
 *
 * Politica de autoplay: el audio solo se inicia tras un gesto explicito del usuario
 * (click o tecla). Los navegadores modernos bloquean autoplay sin interaccion.
 */

import { safeGetStorage, safeSetStorage } from "./utils";

/** Ruta del audio servido desde public/audio/sonido.mp3 */
const AUDIO_PATH = "/audio/sonido.mp3";
/** Volumen objetivo cuando no esta muteado (0.0 a 1.0) */
const TARGET_VOLUME = 0.2;
/** Duracion del fade en ms */
const FADE_DURATION_MS = 200;
/** Clave de localStorage para el estado de mute */
const MUTE_STORAGE_KEY = "alchem_muted";

export class AudioManager {
    private audio: HTMLAudioElement | null = null;
    private muted: boolean = false;
    private volume: number = 0.5;
    private fadeRafId: number | null = null;
    private initialized: boolean = false;
    private sfxCtx: AudioContext | null = null;
    private lastEatSoundAt: number = 0;
    private lastBoostSoundAt: number = 0;

    constructor() {
        // Leer estado de mute desde storage
        const stored = safeGetStorage(MUTE_STORAGE_KEY);
        this.muted = stored === "true";

        const storedVol = safeGetStorage("alchem_volume");
        if (storedVol !== null) {
            const parsed = parseFloat(storedVol);
            this.volume = isNaN(parsed) ? 0.5 : Math.max(0, Math.min(1, parsed));
        } else {
            this.volume = 0.5;
        }

        window.addEventListener("beforeunload", () => {
            if (this.audio && !this.audio.paused) {
                sessionStorage.setItem("alchem_audio_time", String(this.audio.currentTime));
            }
        });
    }

    /**
     * Inicia la reproduccion. Solo debe llamarse tras un gesto del usuario.
     * Idempotente: llamarla multiples veces no crea multiples instancias.
     */
    public start(): void {
        if (this.initialized) return;
        this.initialized = true;

        try {
            this.audio = new Audio(AUDIO_PATH);
            this.audio.loop = true;
            this.audio.volume = 0;

            const storedTime = sessionStorage.getItem("alchem_audio_time");
            if (storedTime !== null) {
                const parsedTime = parseFloat(storedTime);
                if (!isNaN(parsedTime) && parsedTime > 0) {
                    this.audio.currentTime = parsedTime;
                }
            }

            // Intentar reproducir
            const playPromise = this.audio.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        // Fade-in desde 0 al volumen objetivo escalado
                        if (!this.muted) {
                            this.fadeTo(this.volume * 0.4);
                        }
                    })
                    .catch((err) => {
                        console.warn("[Audio] No se pudo iniciar la reproduccion:", err);
                        this.initialized = false;
                    });
            }
        } catch (err) {
            console.warn("[Audio] Error al crear el elemento Audio:", err);
            this.initialized = false;
        }
    }

    /**
     * Alterna mute/unmute con fade suave.
     */
    public toggleMute(): boolean {
        this.muted = !this.muted;
        safeSetStorage(MUTE_STORAGE_KEY, this.muted ? "true" : "false");
        if (this.audio) {
            this.fadeTo(this.muted ? 0 : this.volume * 0.4);
        }
        return this.muted;
    }

    /**
     * Configura el nivel de volumen general (0.0 a 1.0) con persistencia en localStorage.
     */
    public setVolume(val: number): void {
        this.volume = Math.max(0, Math.min(1, isNaN(val) ? 0.5 : val));
        safeSetStorage("alchem_volume", this.volume.toString());
        if (this.audio) {
            if (this.muted) {
                this.audio.volume = 0;
            } else {
                this.fadeTo(this.volume * 0.4);
            }
        }
    }

    /**
     * Obtiene el nivel actual de volumen (0.0 a 1.0).
     */
    public getVolume(): number {
        return this.volume;
    }

    /**
     * Devuelve true si esta muteado.
     */
    public isMuted(): boolean {
        return this.muted;
    }

    /**
     * Fade lineal del volumen actual al target en FADE_DURATION_MS.
     * Cancela cualquier fade en progreso.
     */
    private fadeTo(targetVolume: number): void {
        if (!this.audio) return;
        if (this.fadeRafId !== null) {
            cancelAnimationFrame(this.fadeRafId);
            this.fadeRafId = null;
        }
        const startVolume = this.audio.volume;
        const startTime = performance.now();
        const deltaVolume = targetVolume - startVolume;
        const step = (now: number): void => {
            if (!this.audio) return;
            const elapsed = now - startTime;
            const progress = Math.min(1, elapsed / FADE_DURATION_MS);
            // Clamp del volumen a [0, 1] para evitar IndexSizeError
            const newVolume = startVolume + deltaVolume * progress;
            this.audio.volume = Math.max(0, Math.min(1, newVolume));
            if (progress < 1) {
                this.fadeRafId = requestAnimationFrame(step);
            } else {
                this.fadeRafId = null;
            }
        };
        this.fadeRafId = requestAnimationFrame(step);
    }

    /**
     * Obtiene o inicializa el AudioContext para efectos procedurales,
     * respetando el estado de mute y desbloqueando estados suspendidos.
     */
    private getSfxContext(): AudioContext | null {
        if (this.muted || this.volume <= 0) return null;
        if (!this.sfxCtx) {
            const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
            if (AudioCtx) {
                this.sfxCtx = new AudioCtx();
            }
        }
        if (this.sfxCtx && this.sfxCtx.state === "suspended") {
            this.sfxCtx.resume().catch(() => {});
        }
        return this.sfxCtx;
    }

    /**
     * SFX de recolección de comida (con throttle de 45ms).
     * Oscilador sine de 520Hz a 860Hz en 0.065s con ganancia 0.06 a 0.001.
     */
    public playEatSfx(): void {
        const now = performance.now();
        if (now - this.lastEatSoundAt < 45) return;
        this.lastEatSoundAt = now;

        const ctx = this.getSfxContext();
        if (!ctx) return;

        const t = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(520, t);
        osc.frequency.exponentialRampToValueAtTime(860, t + 0.065);

        gain.gain.setValueAtTime(0.06, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.065);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(t);
        osc.stop(t + 0.065);
    }

    /**
     * SFX de propulsión alquímica (con throttle de 220ms).
     * Oscilador triangle de 160Hz a 95Hz en 0.16s con ganancia 0.08 a 0.001.
     */
    public playBoostSfx(): void {
        const now = performance.now();
        if (now - this.lastBoostSoundAt < 220) return;
        this.lastBoostSoundAt = now;

        const ctx = this.getSfxContext();
        if (!ctx) return;

        const t = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "triangle";
        osc.frequency.setValueAtTime(160, t);
        osc.frequency.exponentialRampToValueAtTime(95, t + 0.16);

        gain.gain.setValueAtTime(0.08, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(t);
        osc.stop(t + 0.16);
    }

    /**
     * SFX de impacto elemental / counter.
     * Oscilador sawtooth de 320Hz a 110Hz en 0.18s con ganancia 0.11 a 0.001.
     */
    public playCounterSfx(): void {
        const ctx = this.getSfxContext();
        if (!ctx) return;

        const t = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(320, t);
        osc.frequency.exponentialRampToValueAtTime(110, t + 0.18);

        gain.gain.setValueAtTime(0.11, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(t);
        osc.stop(t + 0.18);
    }

    /**
     * SFX de combo ofensivo con tono armónico progresivo.
     * Dos osciladores triangle en acorde armónico (base y base * 1.25) durante 0.22s con ganancia 0.10.
     */
    public playComboSfx(combo: number): void {
        const ctx = this.getSfxContext();
        if (!ctx) return;

        const t = ctx.currentTime;
        const baseFreq = Math.min(980, 380 + combo * 65);
        const duration = 0.22;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.10, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
        gain.connect(ctx.destination);

        const osc1 = ctx.createOscillator();
        osc1.type = "triangle";
        osc1.frequency.setValueAtTime(baseFreq, t);
        osc1.connect(gain);
        osc1.start(t);
        osc1.stop(t + duration);

        const osc2 = ctx.createOscillator();
        osc2.type = "triangle";
        osc2.frequency.setValueAtTime(baseFreq * 1.25, t);
        osc2.connect(gain);
        osc2.start(t);
        osc2.stop(t + duration);
    }

    /**
     * SFX de recolección de power-up.
     * Oscilador sine que salta de 587.33Hz (D5) a 880Hz (A5) a los 0.07s y dura 0.22s con ganancia 0.10.
     */
    public playPowerUpSfx(): void {
        const ctx = this.getSfxContext();
        if (!ctx) return;

        const t = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(587.33, t);
        osc.frequency.setValueAtTime(880, t + 0.07);

        gain.gain.setValueAtTime(0.10, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(t);
        osc.stop(t + 0.22);
    }

    /**
     * Reproduce un efecto de desintegración cristalina / implosión alquímica al morir.
     * Usa un arpegio menor descendente limpio (sine + triangle) para sonar mágico y nítido.
     */
    public playDeathSfx(): void {
        const ctx = this.getSfxContext();
        if (!ctx) return;

        const now = ctx.currentTime;
        // Notas de desintegración cristalina (Mi5 -> Do5 -> La4 -> Mi4)
        const notes = [659.25, 523.25, 440.0, 329.63];

        notes.forEach((freq, idx) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = idx % 2 === 0 ? "sine" : "triangle";

            const startAt = now + idx * 0.055;
            const duration = 0.18;

            osc.frequency.setValueAtTime(freq, startAt);
            osc.frequency.exponentialRampToValueAtTime(freq * 0.92, startAt + duration);

            gain.gain.setValueAtTime(0.001, startAt);
            gain.gain.linearRampToValueAtTime(0.09, startAt + 0.015);
            gain.gain.exponentialRampToValueAtTime(0.0008, startAt + duration);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(startAt);
            osc.stop(startAt + duration + 0.01);
        });
    }

    /**
     * SFX de éxito / misión cumplida o victoria en tutorial.
     * Arpegio ascendente brillante en Do Mayor (Do5 -> Mi5 -> Sol5 -> Do6).
     */
    public playSuccessSfx(): void {
        const ctx = this.getSfxContext();
        if (!ctx) return;

        const now = ctx.currentTime;
        const notes = [523.25, 659.25, 783.99, 1046.50];

        notes.forEach((freq, idx) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";

            const startAt = now + idx * 0.07;
            const duration = 0.24;

            osc.frequency.setValueAtTime(freq, startAt);

            gain.gain.setValueAtTime(0.001, startAt);
            gain.gain.linearRampToValueAtTime(0.09 * this.volume, startAt + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0008, startAt + duration);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(startAt);
            osc.stop(startAt + duration);
        });
    }

    /**
     * SFX de activación mágica de poder de Mascota.
     * Sintetiza un acorde de campanilla arpegiada mágica ascendente:
     * C5 (523.25Hz) -> E5 (659.25Hz) -> G5 (783.99Hz) -> B5 (987.77Hz),
     * oscilador sinusoidal, ataque rápido de 10ms y decaimiento suave de 350ms,
     * volumen normalizado clamped [0, 0.4].
     */
    public playPetActivationSound(): void {
        if (this.muted) return;
        const ctx = this.getSfxContext();
        if (!ctx) return;

        const now = ctx.currentTime;
        // Frecuencias para C5 -> E5 -> G5 -> B5
        const notes = [523.25, 659.25, 783.99, 987.77];
        // Volumen normalizado clamped [0, 0.4]
        const targetPeak = Math.max(0, Math.min(0.4, 0.35 * this.volume));
        if (targetPeak <= 0) return;

        notes.forEach((freq, idx) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";

            const startAt = now + idx * 0.055;
            const attackTime = 0.010; // 10ms
            const decayTime = 0.350;  // 350ms
            const totalDuration = attackTime + decayTime;

            osc.frequency.setValueAtTime(freq, startAt);

            gain.gain.setValueAtTime(0.0001, startAt);
            gain.gain.linearRampToValueAtTime(targetPeak, startAt + attackTime);
            gain.gain.exponentialRampToValueAtTime(0.0001, startAt + totalDuration);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(startAt);
            osc.stop(startAt + totalDuration + 0.02);
        });
    }
}

